/**
 * MassEventPolygonEditor.jsx · v3.5.0
 * ─────────────────────────────────────────────────────────────────────
 * Modal fullscreen para dibujar un polígono sobre un mapa Leaflet y crear
 * un evento masivo. Usa `leaflet-draw` (ya presente en package.json) con
 * el hook `useMap()` de react-leaflet, sin depender de react-leaflet-draw.
 */
import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap, FeatureGroup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet-draw";
import { X, Save, Loader2, AlertTriangle, Trash2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Ecuador (Manta) por defecto, para dispatcher regional
const DEFAULT_CENTER = [-0.9482, -80.7301];
const DEFAULT_ZOOM = 8;

function DrawControl({ onCreated, onDeleted, drawnRef }) {
  const map = useMap();
  useEffect(() => {
    const drawn = new L.FeatureGroup().addTo(map);
    drawnRef.current = drawn;
    const drawControl = new L.Control.Draw({
      position: "topleft",
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          drawError: { color: "#DC2626", message: "<strong>Error</strong> – no cruces líneas" },
          shapeOptions: { color: "#DC2626", weight: 3, fillOpacity: 0.25 },
        },
        rectangle: { shapeOptions: { color: "#DC2626", weight: 3, fillOpacity: 0.25 } },
        circle: { shapeOptions: { color: "#DC2626", weight: 3, fillOpacity: 0.25 } },
        marker: false, polyline: false, circlemarker: false,
      },
      edit: { featureGroup: drawn, remove: true },
    });
    map.addControl(drawControl);
    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawn.clearLayers();
      drawn.addLayer(layer);
      // Convertir a GeoJSON Polygon [lng,lat]
      let coords = null;
      if (layer instanceof L.Circle) {
        // Aproximar círculo con 32 puntos
        const center = layer.getLatLng();
        const rmeters = layer.getRadius();
        const pts = [];
        const R = 6378137;
        for (let i = 0; i <= 32; i++) {
          const angle = (i / 32) * 2 * Math.PI;
          const dx = rmeters * Math.cos(angle);
          const dy = rmeters * Math.sin(angle);
          const dLat = dy / R;
          const dLng = dx / (R * Math.cos(center.lat * Math.PI / 180));
          pts.push([center.lng + (dLng * 180 / Math.PI), center.lat + (dLat * 180 / Math.PI)]);
        }
        coords = [pts];
      } else {
        const gj = layer.toGeoJSON();
        coords = gj.geometry.coordinates;
      }
      onCreated({ type: "Polygon", coordinates: coords });
    });
    map.on(L.Draw.Event.DELETED, () => onDeleted());
    return () => {
      map.removeControl(drawControl);
      map.removeLayer(drawn);
    };
    // eslint-disable-next-line
  }, []);
  return null;
}

export default function MassEventPolygonEditor({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("corte_via");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [endsAt, setEndsAt] = useState(() => {
    const d = new Date(); d.setHours(d.getHours() + 4);
    return d.toISOString().slice(0, 16);
  });
  const [geom, setGeom] = useState(null);
  const [saving, setSaving] = useState(false);
  const drawnRef = useRef(null);

  useEffect(() => {
    if (open) {
      setGeom(null); setTitle(""); setDescription("");
    }
  }, [open]);

  if (!open) return null;

  const canSave = title.trim() && geom && startsAt && endsAt && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        category,
        area_type: "polygon",
        polygon_geojson: geom,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
      };
      const { data } = await api.post("/v1/mass-events", body);
      toast.success(`Evento creado · ${data.affected_ot_count} OTs afectadas`);
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "Error al crear evento"));
    } finally {
      setSaving(false);
    }
  };

  const clearGeom = () => {
    drawnRef.current?.clearLayers();
    setGeom(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-stretch bg-black/60" data-testid="mass-event-polygon-modal">
      {/* Panel izquierdo */}
      <aside className="w-[380px] bg-white shadow-2xl flex flex-col">
        <header className="px-4 py-3 bg-[#0A3747] text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#E0E621]/80">Nuevo evento</div>
            <div className="text-sm font-bold">Por polígono</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/10" data-testid="mass-event-modal-close">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className="text-[11px] font-bold text-[#0A3747] block mb-1">Título del evento *</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
                   className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm"
                   placeholder="Ej: Corte de fibra Manta Norte"
                   data-testid="mass-event-title" />
          </div>
          <div>
            <label className="text-[11px] font-bold text-[#0A3747] block mb-1">Categoría</label>
            <select value={category} onChange={e => setCategory(e.target.value)}
                    className="w-full px-2 py-1.5 rounded border border-gray-300 text-sm"
                    data-testid="mass-event-category">
              <option value="corte_via">Corte de vía</option>
              <option value="corte_fibra">Corte de fibra</option>
              <option value="marcha">Marcha / protesta</option>
              <option value="concierto">Concierto / evento</option>
              <option value="clima">Clima / inundación</option>
              <option value="otro">Otro</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] font-bold text-[#0A3747] block mb-1">Inicio *</label>
              <input type="datetime-local" value={startsAt} onChange={e => setStartsAt(e.target.value)}
                     className="w-full px-2 py-1.5 rounded border border-gray-300 text-xs"
                     data-testid="mass-event-starts-at" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-[#0A3747] block mb-1">Fin *</label>
              <input type="datetime-local" value={endsAt} onChange={e => setEndsAt(e.target.value)}
                     className="w-full px-2 py-1.5 rounded border border-gray-300 text-xs"
                     data-testid="mass-event-ends-at" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold text-[#0A3747] block mb-1">Descripción</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)}
                      rows={3} className="w-full px-2 py-1.5 rounded border border-gray-300 text-xs"
                      placeholder="Notas para el equipo…"
                      data-testid="mass-event-description" />
          </div>

          <div className={`p-3 rounded-lg border ${geom ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
            <div className="text-[11px] font-bold flex items-center gap-1.5">
              {geom ? <span className="text-emerald-700">✓ Polígono definido</span> : (
                <>
                  <AlertTriangle size={12} className="text-amber-700" />
                  <span className="text-amber-700">Dibuja un polígono en el mapa</span>
                </>
              )}
            </div>
            <div className="text-[10px] text-gray-600 mt-1">
              Usa la barra superior izquierda del mapa: polígono libre, rectángulo o círculo.
            </div>
            {geom && (
              <button onClick={clearGeom} className="mt-2 text-[10px] font-bold text-red-600 hover:text-red-800 flex items-center gap-1"
                      data-testid="mass-event-clear-geom">
                <Trash2 size={10} /> Borrar y volver a dibujar
              </button>
            )}
          </div>
        </div>

        <footer className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 rounded border border-gray-300 bg-white text-xs font-bold"
                  data-testid="mass-event-cancel">
            Cancelar
          </button>
          <button onClick={submit} disabled={!canSave}
                  className={`flex-1 px-3 py-2 rounded text-xs font-bold flex items-center justify-center gap-1.5 ${
                    canSave ? "bg-[#0A3747] text-[#E0E621]" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  data-testid="mass-event-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Crear + Scan PIP
          </button>
        </footer>
      </aside>

      {/* Mapa */}
      <div className="flex-1 relative">
        <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
          <DrawControl
            onCreated={setGeom}
            onDeleted={() => setGeom(null)}
            drawnRef={drawnRef}
          />
        </MapContainer>
        <div className="absolute top-3 right-3 bg-white/95 rounded-lg shadow-lg border border-gray-200 px-3 py-2 text-[10px]">
          <div className="font-bold text-[#0A3747] mb-0.5">Cómo dibujar</div>
          <div className="text-gray-600">1. Click en 🔷 polígono / rectángulo / círculo (barra izquierda del mapa)</div>
          <div className="text-gray-600">2. Click en cada vértice · doble-click para cerrar</div>
          <div className="text-gray-600">3. Al guardar, todas las OTs dentro se moverán a Revisión</div>
        </div>
      </div>
    </div>
  );
}
